// src/App.js — routing centrale. Le pagine gestionali sono avvolte da AppShell
// (sidebar + topbar); le viste pubbliche/OBS/login restano senza guscio.
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';

// Ordine CSS: token/primitivi → CSS storici per pagina → shell (che deve vincere).
import './styles/tokens.css';
import './App.css';
import './styles/legacy/live-shared-legacy.css';
import './styles/legacy/pilots-legacy.css';
import './styles/legacy/racelive-legacy.css';
import './styles/legacy/circuits-legacy.css';
import './styles/legacy/championships-legacy.css';
import './styles/legacy/pilotlive-legacy.css';
import './styles/legacy/racesetup-legacy.css';
import './styles/legacy/pulse-legacy.css';
import './styles/legacy/livepublic-legacy.css';
import './styles/legacy/training-legacy.css';

import AppShell from './components/AppShell';
import AuthGate from './components/AuthGate';
import FeatureRoute from './components/FeatureRoute';
import Clients from './pages/Clients';
import Home from './pages/Home';
import PilotsPage from './pages/Pilots';
import CarsPage from './pages/Cars';
import CircuitsPage from './pages/Circuits';
import CreateCircuitPage from './pages/CreateCircuit';
import CreateCircuitChooser from './pages/CreateCircuitChooser';
import ManualCircuitEditor from './pages/ManualCircuitEditor';
import RacePage from './pages/Race';
import PilotLive from './pages/PilotLive';
import LivePublic from './pages/LivePublic';
import Championships from './pages/Championships';
import PulsePage from './pages/Pulse';
import ObsOverlay from './pages/ObsOverlay';
import ObsGraphics from './pages/ObsGraphics';
import SocialOverlay from './pages/SocialOverlay';
import PastRaces from './pages/PastRaces';
import Login from './pages/Login';
import Training from './pages/Training';
import Trainings from './pages/Trainings';
import TrainingShared from './pages/TrainingShared';
import LandingSetup from './pages/LandingSetup';
import Sponsors from './pages/Sponsors';
import DeviceDiagnostics from './pages/DeviceDiagnostics';
import SponsorOverlay from './pages/SponsorOverlay';

// Lazy-load del dettaglio gara: pagina pesante, caricata solo on-demand
const RaceDetail = React.lazy(() => import('./pages/RaceDetail'));

const API_BASE = process.env.REACT_APP_API_BASE || (window.location.protocol === 'https:' ? '' : `http://${window.location.hostname}:5000`);

// Scroll automatico all'inizio della pagina ad ogni cambio route
function ScrollToTop() {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Route senza guscio: viste pubbliche, overlay OBS, pagina pilota, link
// allenamento condiviso, login e landing Pulse.
function isBareRoute(pathname) {
  return pathname.startsWith('/obs')
    || pathname.startsWith('/live/')
    || pathname.startsWith('/training/')
    || pathname.startsWith('/pilot/')
    || pathname.startsWith('/pulse')
    || pathname === '/login';
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthGate />
      <AppContent />
    </BrowserRouter>
  );
}

function AppContent() {
  const location = useLocation();
  const bare = isBareRoute(location.pathname);

  const routes = (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/circuiti" element={<CircuitsPage apiBase={API_BASE} />} />
      <Route path="/circuiti/crea" element={<CreateCircuitChooser />} />
      <Route path="/circuiti/crea/automatica" element={<CreateCircuitPage apiBase={API_BASE} />} />
      <Route path="/circuiti/crea/manuale" element={<ManualCircuitEditor />} />
      <Route path="/circuiti/modifica/:id" element={<ManualCircuitEditor />} />
      <Route path="/piloti" element={<PilotsPage apiBase={API_BASE} />} />
      <Route path="/auto" element={<FeatureRoute feature="training"><CarsPage /></FeatureRoute>} />
      <Route path="/campionati" element={<Championships />} />
      <Route path="/gare-passate" element={<FeatureRoute feature="race"><PastRaces /></FeatureRoute>} />
      <Route
        path="/gare-passate/:folder"
        element={
          <Suspense fallback={<div className="rs-page"><div className="rs-empty"><div className="rs-spinner" /><strong>Caricamento dettaglio gara…</strong></div></div>}>
            <RaceDetail />
          </Suspense>
        }
      />
      <Route path="/race" element={<FeatureRoute feature="race"><RacePage /></FeatureRoute>} />
      <Route path="/allenamento" element={<FeatureRoute feature="training"><Training /></FeatureRoute>} />
      <Route path="/allenamenti-passati" element={<FeatureRoute feature="training"><Trainings /></FeatureRoute>} />
      <Route path="/training/:id" element={<TrainingShared />} />
      <Route path="/pilot/:mac" element={<PilotLive />} />
      <Route path="/live/:raceId" element={<LivePublic />} />
      <Route path="/pulse" element={<PulsePage apiBase={API_BASE} />} />
      <Route path="/landing-gara" element={<FeatureRoute feature="race"><LandingSetup /></FeatureRoute>} />
      <Route path="/clienti" element={<Clients />} />
      <Route path="/diagnostica" element={<DeviceDiagnostics />} />
      <Route path="/sponsor" element={<Sponsors />} />
      <Route path="/obs/sponsors" element={<SponsorOverlay variant="carousel" />} />
      <Route path="/obs/sponsors/banner" element={<SponsorOverlay variant="banner" />} />
      <Route path="/obs/sponsors/ticker" element={<SponsorOverlay variant="ticker" />} />
      <Route path="/obs/sponsors/flash" element={<SponsorOverlay variant="flash" />} />
      <Route path="/obs/sponsor/:id" element={<SponsorOverlay variant="single" />} />
      <Route path="/obs/graphics" element={<ObsGraphics />} />
      {/* Dirette social simulate per i video promozionali (fondo verde) */}
      <Route path="/obs/social" element={<SocialOverlay />} />
      <Route path="/obs/social/:network" element={<SocialOverlay />} />
      <Route path="/obs/graphics/:scene" element={<ObsGraphics />} />
      <Route path="/obs" element={<ObsOverlay />} />
      <Route path="/obs/:overlayType" element={<ObsOverlay />} />
      <Route path="/login" element={<Login />} />
      <Route path="*" element={<Home />} />
    </Routes>
  );

  return (
    <>
      <ScrollToTop />
      {bare ? routes : <AppShell>{routes}</AppShell>}
    </>
  );
}
